import { useParams } from 'react-router-dom';
import { checkVariant, summarise, type VariantInput } from '@motorcity/shared';
import { useBrandTree, useSaveVariant } from '../api/hooks';
import { Breadcrumbs, ErrorNote, PageHeader } from '../components/Layout';
import { LocaleSwitch, countMissing } from '../components/EditLocale';
import { LiveWarning, ReadinessPanel } from '../components/Readiness';
import { FormSkeleton } from '../components/ui/primitives';
import { errorMessage, useToast } from '../components/ui/Toast';
import { SaveBar } from '../components/SaveBar';
import { VehicleForm } from '../components/VehicleForm';
import { useDraft, useUnsavedWarning } from '../hooks/useDraft';
import type { ApiError } from '../api/client';

export function VariantPage() {
  const { brandId = '', modelId = '', variantId = '' } = useParams();
  const tree = useBrandTree(brandId);
  const save = useSaveVariant(brandId, modelId, variantId);
  const toast = useToast();

  const variant = tree.data?.variants.find(
    (v) => v.modelId === modelId && v.variantId === variantId,
  );
  const serverValue: VariantInput | undefined = variant
    ? {
        name: variant.name,
        intro: variant.intro,
        description: variant.description,
        tacticalImage: variant.tacticalImage,
        iconFront: variant.iconFront,
        iconSide: variant.iconSide,
        usps: variant.usps,
        startingPrice: variant.startingPrice,
        startingEmi: variant.startingEmi,
        sortOrder: variant.sortOrder,
        status: variant.status,
      }
    : undefined;

  const { draft, setDraft, dirty, commit, reset } = useDraft(serverValue);
  useUnsavedWarning(dirty);

  if (tree.isLoading || (!tree.error && !draft)) {
    return (
      <div className="page">
        <PageHeader title="Loading…" />
        <FormSkeleton />
      </div>
    );
  }
  if (tree.error) return <ErrorNote error={tree.error} />;
  if (!tree.data || !variant || !draft) return <ErrorNote error={new Error('Variant not found')} />;

  const missing = countMissing([draft.name, draft.intro, draft.description]);
  const readiness = summarise(checkVariant(draft));

  const model = tree.data.models.find((m) => m.modelId === modelId);

  return (
    <div className="page">
      <Breadcrumbs
        trail={[
          { label: 'Brands', to: '/brands' },
          { label: tree.data.brand.name.en, to: `/brands/${brandId}` },
          { label: model?.name.en ?? modelId, to: `/brands/${brandId}/models/${modelId}` },
          { label: draft.name.en || variantId },
        ]}
      />

      <PageHeader title={draft.name.en || variantId} slug={variantId}>
        <LocaleSwitch missing={missing} />
      </PageHeader>

      <ReadinessPanel summary={readiness} status={draft.status} entity="variant" />

      <VehicleForm
        value={draft}
        statusWarning={<LiveWarning summary={readiness} />}
        onChange={setDraft}
        assetPrefix={`brands/${brandId}/models/${modelId}/variants/${variantId}`}
        fieldErrors={(save.error as ApiError | undefined)?.fields}
      />

      <SaveBar
        dirty={dirty}
        saving={save.isPending}
        error={save.error}
        onReset={reset}
        onSave={() =>
          save.mutate(draft, {
            onSuccess: () => {
              commit(draft);
              toast.success('Variant saved');
            },
            onError: (err) => toast.error(errorMessage(err)),
          })
        }
      />
    </div>
  );
}
